import { Skeleton } from "@/components/ui/skeleton";

/**
 * Skeleton for the list of tables (DatabasesClient empty/loading state).
 */
export function TablesListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="animate-in fade-in divide-ds-neutral-200 dark:divide-ds-neutral-800 divide-y duration-300">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4">
          <Skeleton className="h-10 w-10 flex-shrink-0 rounded-lg" />
          <div className="flex flex-1 flex-col gap-1">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for a schema table (column list).
 */
export function SchemaTableSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="animate-in fade-in duration-300">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-ds-border-default text-ds-text-muted border-b text-left text-xs font-medium uppercase">
            <th className="px-6 py-3">Column</th>
            <th className="px-6 py-3">Type</th>
            <th className="px-6 py-3">Primary Key</th>
            <th className="px-6 py-3">Not Null</th>
            <th className="px-6 py-3">Default</th>
            <th className="w-20 px-6 py-3" />
          </tr>
        </thead>
        <tbody className="divide-ds-neutral-200 dark:divide-ds-neutral-800 divide-y">
          {Array.from({ length: count }).map((_, i) => (
            <tr key={i}>
              <td className="px-6 py-3">
                <Skeleton className="h-4 w-32" />
              </td>
              <td className="px-6 py-3">
                <Skeleton className="h-4 w-20" />
              </td>
              <td className="px-6 py-3">
                <Skeleton className="h-4 w-4 rounded" />
              </td>
              <td className="px-6 py-3">
                <Skeleton className="h-4 w-4 rounded" />
              </td>
              <td className="px-6 py-3">
                <Skeleton className="h-4 w-16" />
              </td>
              <td className="px-6 py-3">
                <Skeleton className="h-7 w-7 rounded" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Skeleton for a data grid (table rows). Generic column count.
 */
export function DataTableGridSkeleton({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="animate-in fade-in overflow-x-auto duration-300">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-ds-border-default text-ds-text-muted border-b text-left text-xs font-medium uppercase">
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3">
                <Skeleton className="h-3 w-16" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-ds-neutral-200 dark:divide-ds-neutral-800 divide-y">
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c} className="px-4 py-3">
                  <Skeleton className="h-4" style={{ width: `${40 + ((r * 13 + c * 7) % 50)}%` }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
